import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { QuickStart } from '../components/QuickStart';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import { PageHeader } from '../components/PageHeader';

export function Insights() {
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedView, setSelectedView] = useState('overview');
  const [selectedCluster, setSelectedCluster] = useState('all');
  
  // Quick Start Guides for GCP Agent Deployment
  const quickStartGuides = [
    {
      title: "Agent Performance",
      description: "Monitor deployed AI agents' performance metrics, resource usage, and response times across your GCP clusters.",
      keywords: ["Metrics", "Performance", "Latency"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      title: "Resource Utilization",
      description: "Track CPU, memory, and network usage across your deployed agents to optimize cost and performance.",
      keywords: ["Resources", "CPU", "Memory"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Cost Analysis",
      description: "Understand your GCP spending by agent type, region, and usage patterns to optimize your cloud budget.",
      keywords: ["Cost", "Budget", "Optimization"],
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  // GCP regions data
  const gcpRegions = [
    { id: 'us-central1', name: 'Iowa (us-central1)' },
    { id: 'us-east1', name: 'South Carolina (us-east1)' },
    { id: 'us-west1', name: 'Oregon (us-west1)' },
    { id: 'europe-west1', name: 'Belgium (europe-west1)' },
    { id: 'asia-east1', name: 'Taiwan (asia-east1)' }
  ];

  // Deployed Agent data
  const deployedAgentsData = {
    'Code Reviewer': {
      instances: 12,
      activeInstances: 8,
      region: 'us-central1',
      machineType: 'e2-small',
      uptime: '99.97%',
      averageLatency: '245ms',
      cpuUtilization: 42,
      memoryUtilization: 67,
      apiRequests: 15423,
      costPerDay: 12.45,
      status: 'Healthy'
    },
    'Content Studio': {
      instances: 8,
      activeInstances: 6,
      region: 'us-west1',
      machineType: 'e2-medium',
      uptime: '99.85%',
      averageLatency: '312ms',
      cpuUtilization: 58,
      memoryUtilization: 72,
      apiRequests: 23487,
      costPerDay: 18.92,
      status: 'Healthy'
    },
    'Sales Agent': {
      instances: 5,
      activeInstances: 3,
      region: 'europe-west1',
      machineType: 'e2-standard-2',
      uptime: '99.92%',
      averageLatency: '276ms',
      cpuUtilization: 51,
      memoryUtilization: 64,
      apiRequests: 8754,
      costPerDay: 15.28,
      status: 'Warning'
    },
    'Grant Writer': {
      instances: 3,
      activeInstances: 2,
      region: 'us-east1',
      machineType: 'e2-small',
      uptime: '100%',
      averageLatency: '187ms',
      cpuUtilization: 34,
      memoryUtilization: 52,
      apiRequests: 3256,
      costPerDay: 7.15,
      status: 'Healthy'
    },
    'Research Assistant': {
      instances: 4,
      activeInstances: 4,
      region: 'us-central1',
      machineType: 'e2-medium',
      uptime: '99.75%',
      averageLatency: '378ms',
      cpuUtilization: 76,
      memoryUtilization: 82,
      apiRequests: 12589,
      costPerDay: 16.32,
      status: 'Critical'
    },
    'Lesson Plan Creator': {
      instances: 2,
      activeInstances: 1,
      region: 'asia-east1',
      machineType: 'e2-small',
      uptime: '99.96%',
      averageLatency: '254ms',
      cpuUtilization: 28,
      memoryUtilization: 45,
      apiRequests: 1876,
      costPerDay: 5.84,
      status: 'Healthy'
    }
  };

  const agentPerformanceMetrics = [
    {
      name: 'Code Reviewer',
      requests: 15423,
      success: 15245,
      errors: 178,
      averageResponseTime: 245,
      p95ResponseTime: 412,
      successRate: 98.84
    },
    {
      name: 'Content Studio',
      requests: 23487,
      success: 23123,
      errors: 364,
      averageResponseTime: 312,
      p95ResponseTime: 576,
      successRate: 98.45
    },
    {
      name: 'Sales Agent',
      requests: 8754,
      success: 8532,
      errors: 222,
      averageResponseTime: 276,
      p95ResponseTime: 487,
      successRate: 97.46
    },
    {
      name: 'Grant Writer',
      requests: 3256,
      success: 3231,
      errors: 25,
      averageResponseTime: 187,
      p95ResponseTime: 324,
      successRate: 99.23
    },
    {
      name: 'Research Assistant',
      requests: 12589,
      success: 12267,
      errors: 322,
      averageResponseTime: 378,
      p95ResponseTime: 654,
      successRate: 97.44
    },
    {
      name: 'Lesson Plan Creator',
      requests: 1876,
      success: 1854,
      errors: 22,
      averageResponseTime: 254,
      p95ResponseTime: 412,
      successRate: 98.83
    }
  ];

  // Resource usage over time
  const resourceTrends = [
    { time: '00:00', cpu: 45, memory: 58, network: 32, instances: 18 },
    { time: '04:00', cpu: 38, memory: 52, network: 24, instances: 14 },
    { time: '08:00', cpu: 57, memory: 64, network: 46, instances: 21 },
    { time: '12:00', cpu: 68, memory: 72, network: 63, instances: 24 },
    { time: '16:00', cpu: 76, memory: 78, network: 72, instances: 24 },
    { time: '20:00', cpu: 62, memory: 68, network: 54, instances: 20 }
  ];

  // Cost breakdown data
  const costBreakdownData = [
    { name: 'Compute Engine', value: 432.56 },
    { name: 'Cloud Storage', value: 128.92 },
    { name: 'Network Egress', value: 76.45 },
    { name: 'Cloud AI APIs', value: 215.78 },
    { name: 'Other Services', value: 42.35 }
  ];

  // Cost by region data
  const costByRegionData = [
    { name: 'us-central1', value: 345.67 },
    { name: 'us-east1', value: 178.32 },
    { name: 'us-west1', value: 224.56 },
    { name: 'europe-west1', value: 98.43 },
    { name: 'asia-east1', value: 48.78 }
  ];

  // API call trends
  const apiCallTrends = [
    { date: 'Mon', calls: 42567, success: 41987, latency: 254 },
    { date: 'Tue', calls: 46789, success: 46123, latency: 267 },
    { date: 'Wed', calls: 54321, success: 53567, latency: 283 },
    { date: 'Thu', calls: 51234, success: 50654, latency: 276 },
    { date: 'Fri', calls: 58765, success: 57876, latency: 298 },
    { date: 'Sat', calls: 32456, success: 32011, latency: 245 },
    { date: 'Sun', calls: 28765, success: 28453, latency: 232 }
  ];

  // System health data
  const systemHealthData = {
    deployedAgents: 34,
    activeAgents: 24,
    totalRegions: 5,
    totalApiCalls: '253,385',
    averageLatency: '278ms',
    cpuUtilization: '57%',
    memoryUtilization: '68%',
    totalCostToday: '$78.56',
    uptimePercentage: '99.93%'
  };

  // Clusters data
  const clustersData = [
    { id: 'prod-cluster-1', name: 'Production Primary', agents: 15, status: 'Healthy', region: 'us-central1' },
    { id: 'prod-cluster-2', name: 'Production Secondary', agents: 8, status: 'Healthy', region: 'us-east1' },
    { id: 'staging-cluster-1', name: 'Staging', agents: 6, status: 'Warning', region: 'us-west1' },
    { id: 'dev-cluster-1', name: 'Development', agents: 5, status: 'Healthy', region: 'us-west1' },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="AI Agent Insights"
          subtitle="Monitor performance, resource usage, and costs across your AI deployments"
          actions={
            <>
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
              <button className="px-4 py-2 bg-blue-50 text-blue-600 border border-blue-100 rounded-lg text-sm hover:bg-blue-100">
                Export Report
              </button>
            </>
          }
        />

        {/* System Health Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Deployed Agents</h3>
              <span className="text-green-500">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
            </div>
            <div className="mt-2 flex items-baseline">
              <p className="text-2xl font-semibold text-gray-900">{systemHealthData.deployedAgents}</p>
              <p className="ml-2 text-sm text-gray-500">/ {systemHealthData.activeAgents} active</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Total API Calls</h3>
              <span className="text-blue-500">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </span>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-semibold text-gray-900">{systemHealthData.totalApiCalls}</p>
              <p className="text-sm text-gray-500">Avg. latency {systemHealthData.averageLatency}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Resource Usage</h3>
              <span className="text-yellow-500">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-semibold text-gray-900">{systemHealthData.cpuUtilization}</p>
              <p className="text-sm text-gray-500">CPU / {systemHealthData.memoryUtilization} Memory</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-500">Cost Today</h3>
              <span className="text-purple-500">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
            </div>
            <div className="mt-2">
              <p className="text-2xl font-semibold text-gray-900">{systemHealthData.totalCostToday}</p>
              <p className="text-sm text-gray-500">Across {systemHealthData.totalRegions} regions</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content Area */}
          <div className="lg:w-2/3 space-y-6">
            {/* Performance Metrics */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">Performance Metrics</h2>
                  <p className="mt-1 text-sm text-gray-500">API calls and response times across all agents</p>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={apiCallTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" stroke="#6B7280" />
                    <YAxis stroke="#6B7280" />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="calls" stroke="#3B82F6" name="Total Calls" />
                    <Line type="monotone" dataKey="success" stroke="#10B981" name="Successful" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Resource Usage Trends */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">Resource Usage Trends</h2>
                  <p className="mt-1 text-sm text-gray-500">CPU, memory, and network utilization over time</p>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={resourceTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="time" stroke="#6B7280" />
                    <YAxis stroke="#6B7280" />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="cpu" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} name="CPU %" />
                    <Area type="monotone" dataKey="memory" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.2} name="Memory %" />
                    <Area type="monotone" dataKey="network" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.2} name="Network %" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-1/3 space-y-6">
            {/* Cost Breakdown */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">Cost Breakdown</h2>
                  <p className="mt-1 text-sm text-gray-500">Service-wise cost distribution</p>
                </div>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={costBreakdownData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#3B82F6"
                    >
                      {costBreakdownData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#6B7280'][index]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Agent Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <div className="px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-900">Agent Status</h2>
                  <p className="mt-1 text-sm text-gray-500">Current state of deployed agents</p>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {Object.entries(deployedAgentsData).map(([name, data]) => (
                  <div key={name} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{name}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {data.activeInstances} of {data.instances} instances active
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        data.status === 'Healthy' 
                          ? 'bg-green-100 text-green-800' 
                          : data.status === 'Warning'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {data.status}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">CPU:</span>
                        <span className="ml-1 text-gray-900">{data.cpuUtilization}%</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Memory:</span>
                        <span className="ml-1 text-gray-900">{data.memoryUtilization}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 